import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaSpinner, FaRegCircle, FaCalendarAlt } from 'react-icons/fa';
import './Roadmap.css';

const STATUS_META = {
  'done': { label: 'Ukończone', icon: <FaCheckCircle />, cls: 'goal--done' },
  'completed': { label: 'Ukończone', icon: <FaCheckCircle />, cls: 'goal--done' },
  'in-progress': { label: 'W trakcie', icon: <FaSpinner />, cls: 'goal--active' },
  'active': { label: 'W trakcie', icon: <FaSpinner />, cls: 'goal--active' },
  'planned': { label: 'Planowane', icon: <FaRegCircle />, cls: 'goal--planned' },
};

const getStatusMeta = (status) =>
  STATUS_META[status?.toLowerCase()] || STATUS_META['planned'];

export default function Roadmap({ roadmap: ctxRoadmap }) {
  const [roadmap, setRoadmap] = useState([]);

  useEffect(() => {
    setRoadmap(ctxRoadmap || []);
  }, [ctxRoadmap]);

  const totalGoals = roadmap.reduce((n, m) => n + (m.goals?.length || 0), 0);
  const doneGoals = roadmap.reduce(
    (n, m) =>
      n + (m.goals?.filter((g) => g.status === 'done' || g.status === 'completed').length || 0),
    0
  );
  const activeGoals = roadmap.reduce(
    (n, m) => n + (m.goals?.filter((g) => g.status === 'in-progress').length || 0),
    0
  );

  return (
    <div className="roadmap-page">
      {/* Header */}
      <motion.div
        className="roadmap-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <div className="roadmap-header__inner">
          <span className="roadmap-header__label">Planowanie</span>
          <h1 className="roadmap-header__title">Roadmap</h1>
          <p className="roadmap-header__subtitle">
            Cele i kamienie milowe dla aktywnych projektów.
          </p>
        </div>

        {/* Stats */}
        <div className="roadmap-stats">
          <div className="roadmap-stat">
            <span className="roadmap-stat__value">{totalGoals}</span>
            <span className="roadmap-stat__label">Łącznie celów</span>
          </div>
          <div className="roadmap-stat roadmap-stat--active">
            <span className="roadmap-stat__value">{activeGoals}</span>
            <span className="roadmap-stat__label">W trakcie</span>
          </div>
          <div className="roadmap-stat roadmap-stat--done">
            <span className="roadmap-stat__value">{doneGoals}</span>
            <span className="roadmap-stat__label">Ukończone</span>
          </div>
        </div>
      </motion.div>

      {/* Timeline */}
      <div className="roadmap-timeline">
        {roadmap.length === 0 && (
          <div className="roadmap-empty">
            <FaCalendarAlt />
            <p>Brak danych roadmapy.</p>
          </div>
        )}

        {roadmap.map((milestone, idx) => (
          <motion.div
            key={milestone.id}
            className="milestone"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: idx * 0.08 }}
          >
            {/* Month marker */}
            <div className="milestone__marker">
              <div className="milestone__dot" />
              <div className="milestone__line" />
            </div>

            {/* Content */}
            <div className="milestone__content">
              <h2 className="milestone__month">
                <FaCalendarAlt className="milestone__month-icon" />
                {milestone.month}
              </h2>

              <div className="milestone__goals">
                {milestone.goals?.map((goal, gIdx) => {
                  const meta = getStatusMeta(goal.status);
                  return (
                    <motion.div
                      key={gIdx}
                      className={`goal ${meta.cls}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: idx * 0.08 + gIdx * 0.07 }}
                    >
                      <div className="goal__icon">{meta.icon}</div>
                      <div className="goal__body">
                        <h3 className="goal__title">{goal.title}</h3>
                        {goal.desc && <p className="goal__desc">{goal.desc}</p>}
                      </div>
                      <span className="goal__badge">{meta.label}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
